import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Icon, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import qs from 'qs';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton (pages/clients/index.js).
export const CLIENT_COLUMNS = [
  { area: 'client', label: 'Client', width: 'minmax(230px, 1.5fr)', bone: 'person' },
  { area: 'contact', label: 'Contact', width: 'minmax(190px, 1.1fr)', icon: 'envelope' },
  { area: 'location', label: 'Location', width: 'minmax(200px, 1.3fr)', icon: 'map-marker' },
  { area: 'work', label: 'Open work', width: '136px', bone: 'short', icon: 'clipboard' },
  { area: 'notes', label: 'Notes', width: 'minmax(180px, 1.4fr)', bone: 'single' },
  { area: 'actions', label: '', width: '64px', bone: 'none' },
];

export const CLIENT_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: [
    'client actions',
    'contact contact',
    'location location',
    'work work',
    'notes notes',
  ],
  footer: ['notes'],
};

export const CLIENT_MIN_TABLE_WIDTH = 1120;

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// Initials from the words that contain letters, so "(New) City Hall" is "NC"
// and "1000039298 Ont Inc" is "OI" rather than "(C" or "1O".
const clientInitials = (name) => {
  const words = String(name || '')
    .split(/\s+/)
    .map((word) => word.replace(/[^A-Za-z]/g, ''))
    .filter(Boolean);
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '#';
};

// Names that only differ in punctuation, case or word order ("Brak, Len" vs
// "Len Brak") count as the same, so the legal name only shows when it adds
// something.
const sameName = (a, b) => {
  const words = (value) => String(value || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean).sort().join(' ');
  return words(a) === words(b);
};

const luhn = (digits) => {
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let digit = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

// Card numbers and security codes typed into notes stay out of the list view
// ("•••• 3086"); the full note is still on the client's page.
const maskPaymentDetails = (text) =>
  String(text || '')
    // "4510 1560 2759 0715" style, whatever the checksum says.
    .replace(/\b\d{4}[ -]\d{4}[ -]\d{4}[ -]\d{4}\b/g, (match) => `•••• ${match.slice(-4)}`)
    .replace(/\b(?:\d[ -]?){12,18}\d\b/g, (match) => {
      const digits = match.replace(/\D/g, '');
      return luhn(digits) ? `•••• ${digits.slice(-4)}` : match;
    })
    .replace(/\b(CVV|CVC|CSC)(\s*:?\s*)\d{3,4}\b/gi, (match, label, gap) => `${label}${gap}•••`)
    // An unlabelled code right after a masked card's expiry ("02/27 755").
    .replace(/(•••• \d{4}\D{0,12}\d{2}\/\d{2,4}\s+)\d{3,4}\b/g, '$1•••');

// Do-not-service outranks an alert; both carry the office's reason.
const heads_up = (attributes) => {
  if (attributes.do_not_service) {
    return { tone: 'red', label: 'Do not service', reason: attributes.do_not_service_reason, accent: '#d92d20' };
  }
  if (attributes.is_on_alert) {
    return { tone: 'amber', label: 'On alert', reason: attributes.on_alert_reason, accent: '#f79009' };
  }
  return null;
};

const openWork = (attributes) => {
  const workOrders = (attributes.draft_work_reports_count || 0) + (attributes.submitted_work_reports_count || 0);
  const estimates = (attributes.draft_estimates_count || 0) + (attributes.submitted_estimates_count || 0);
  return [
    workOrders > 0 && plural(workOrders, 'work order'),
    estimates > 0 && plural(estimates, 'estimate'),
  ].filter(Boolean);
};

@inject('store', 'clientStore', 'viewStore') @observer
class ClientsTable extends Component {

  @observable isDeleteDialogVisible = false;
  @observable client;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        clientSearch
      }
    } = this.props;
    this.props.viewStore.clientSearch = {
      ...clientSearch,
      page: newPage
    };
    this.props.viewStore.clients();
  }

  // Empty list's "Clear search": back to all clients, page 1.
  clearSearch() {
    const { viewStore } = this.props;
    viewStore.clientSearch = { ...viewStore.clientSearch, term: '', page: 1 };
    viewStore.bumpListFilters();
    viewStore.clients();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.clientStore.deleteClient(this.client)
      .then(this.onSuccessfulDeleteClient.bind(this))
      .catch(this.onFailedClientDelete.bind(this))
  }

  onSuccessfulDeleteClient(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Client deleted successfully!', intent: Intent.SUCCESS })
    this.client = null;
    this.isDeleteDialogVisible = false;
  }

  onFailedClientDelete(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete client!', intent: Intent.DANGER })
  }

  handleClientClick(event, client) {
    switch (event.label) {
      case 'Edit':
        this.client = client;
        this.props.viewStore.clientShow({uuid: client.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.client = client;
        break;
      default:
    }
  }

  render() {
    let {
      viewStore,
      clientStore: {
        clients: {
          objects,
          meta
        }
      }
    } = this.props;
    const { clientSearch } = viewStore;
    const term = clientSearch.term && clientSearch.term !== '*' ? clientSearch.term : '';

    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="clients"
          term={term}
          onClearFilters={this.clearSearch.bind(this)}
        />
      );
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left responsive-pagination">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={CLIENT_COLUMNS}
          cards={CLIENT_CARDS}
          minTableWidth={CLIENT_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="clients"
          note={DataList.sortNote(clientSearch.order)}
          className="dl--accented"
        >
          {objects.map((client) => {
            const attributes = client.attributes;
            const { email, phone } = attributes;
            const flag = heads_up(attributes);
            const locations = attributes.locations || [];
            const work = openWork(attributes);
            const legal = attributes.legal_name && !sameName(attributes.legal_name, attributes.name)
              ? attributes.legal_name
              : null;
            const notes = attributes.description ? maskPaymentDetails(attributes.description) : null;
            return (
              <DataList.Row key={client.id} accent={flag ? flag.accent : undefined}>
                <DataList.Cell area="client">
                  <div className="client-row">
                    <span className="client-row__avatar" aria-hidden="true">{clientInitials(attributes.name)}</span>
                    <div className="dl-lines">
                      <DataList.RowLink
                        href={`/dashboard/clients/${attributes.id}?${qs.stringify(viewStore.clientShowSearch, { encode: false })}`}
                        label={`Client ${attributes.name}`}
                      >
                        {attributes.name}
                      </DataList.RowLink>
                      <div className="dl-muted dl-truncate client-row__meta">
                        {attributes.identifier && <span>#{attributes.identifier}</span>}
                        {legal && <span title={`Legal name: ${legal}`}>{legal}</span>}
                      </div>
                      {flag && (
                        <div
                          className={`client-row__reason client-row__reason--${flag.tone}`}
                          title={flag.reason ? `${flag.label}: ${flag.reason}` : flag.label}
                        >
                          <Icon icon={flag.tone === 'red' ? 'disable' : 'warning-sign'} iconSize={12} />
                          <span className="dl-truncate">{flag.reason ? `${flag.label}: ${flag.reason}` : flag.label}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </DataList.Cell>
                <DataList.Cell area="contact">
                  {email || phone ? (
                    <div className="dl-lines">
                      {email && (
                        <a className="client-row__contact dl-raised dl-truncate" href={`mailto:${email}`} title={email}>{email}</a>
                      )}
                      {phone && (
                        <a className={`client-row__contact dl-raised ${email ? 'client-row__contact--muted' : ''}`} href={`tel:${phone}`}>{phone}</a>
                      )}
                    </div>
                  ) : (
                    <DataList.Muted>No contact details</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="location">
                  {locations.length > 0 ? (
                    <DataList.Lines
                      primary={locations[0].full_address}
                      strong={false}
                      secondary={locations.length > 1 ? `+${plural(locations.length - 1, 'more location')}` : null}
                    />
                  ) : (
                    <DataList.Muted>No location yet</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="work">
                  {work.length > 0 && <DataList.Lines primary={work[0]} secondary={work[1]} />}
                </DataList.Cell>
                <DataList.Cell area="notes">
                  {notes && (
                    <div className="dl-muted dl-truncate" title={notes}>{notes}</div>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit client', onClick: () => viewStore.clientShow({ uuid: attributes.id }) },
                      { icon: 'trash', label: 'Delete client', intent: 'danger', onClick: () => this.handleClientClick({ label: 'Delete' }, client) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            All associated data of this client (such as work orders, equipments, timesheet entries, jobs etc) will also be deleted. Are you sure you want to do this?
          </p>
        </Alert>
      </React.Fragment>
    )
  }
}

export default ClientsTable;